import { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Download, Server } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMe } from '@/hooks/use-me';
import { AssetType } from '@/siteview/entities/asset-type';
import { useAssetsFilters } from '@/siteview/hooks/use-assets-filters';
import { useCurrentAssets } from '@/siteview/hooks/use-current-assets';
import { singular } from '@/utils/format/label';
import { formatPercentage } from '@/utils/format/number';

import { alertStatusColor } from '../colors/alert-status-color';
import { imbalanceSocColor } from '../colors/imbalance-soc';
import { temperatureColor } from '../colors/temperature-color';
import { assetSocOffset } from '../entities/asset';
import { ViewFiltersMode } from '../entities/view-filters';
import { useViewFilters } from '../hooks/use-view-filters';

interface RackInfoPanelProps {
  isOpen: boolean;
  onToggle: (open: boolean) => void;
}

export function RackInfoSidebar({ isOpen, onToggle }: RackInfoPanelProps) {
  const [viewFilters] = useViewFilters();
  const [assetsFilters] = useAssetsFilters();
  const me = useMe();

  const viewModeToColumn: Partial<Record<ViewFiltersMode, string>> = {
    [ViewFiltersMode.IMBALANCE_SOC]: 'imbalance',
    [ViewFiltersMode.ALERT_STATUS]: 'alertStatus',
    [ViewFiltersMode.ACCURE_SOC]: 'accureSoc',
    [ViewFiltersMode.BMS_SOC]: 'bmsSoc',
    [ViewFiltersMode.SOC_OFFSET]: 'socOffset',
    [ViewFiltersMode.TEMPERATURE]: 'temperature',
  };

  const columnOptions = [
    { value: 'imbalance', label: 'Imbalance SOC' },
    { value: 'alertStatus', label: 'Alert Status' },
    { value: 'accureSoc', label: 'Accure SOC' },
    { value: 'bmsSoc', label: 'BMS SOC' },
    { value: 'socOffset', label: 'SOC Offset' },
    { value: 'temperature', label: 'Temperature' },
  ];

  const selectedColumn = viewModeToColumn[viewFilters.viewMode] || 'imbalance';

  const selectedColumnLabel =
    columnOptions.find((opt) => opt.value === selectedColumn)?.label ||
    selectedColumn;

  function getCellValue(asset: any) {
    switch (selectedColumn) {
      case 'imbalance': {
        return asset.imbalanceSoc == null
          ? '—'
          : formatPercentage(asset.imbalanceSoc, 0, 1);
      }
      case 'alertStatus': {
        return asset.alertStatus ?? '—';
      }
      case 'accureSoc': {
        return asset.accureSoc != null
          ? formatPercentage(asset.accureSoc, 0, 1)
          : '—';
      }
      case 'bmsSoc': {
        return asset.bmsSoc != null
          ? formatPercentage(asset.bmsSoc, 0, 1)
          : '—';
      }
      case 'socOffset': {
        const offset = assetSocOffset(asset);
        return offset != null ? formatPercentage(offset, 0, 1) : '—';
      }
      case 'temperature': {
        return asset.temperature != null ? `${asset.temperature}°C` : '-';
      }
      default: {
        return '—';
      }
    }
  }

  function getCellStyle(asset: any) {
    switch (selectedColumn) {
      case 'imbalance':
        return asset.imbalanceSoc != null
          ? { color: imbalanceSocColor(asset.imbalanceSoc, 0.75) }
          : {};
      case 'alertStatus':
        return asset.alertStatus != null
          ? { color: alertStatusColor(asset.alertStatus) }
          : {};
      case 'socOffset': {
        const offset = assetSocOffset(asset);
        return offset != null ? { color: imbalanceSocColor(offset, 0.75) } : {};
      }
      case 'temperature':
        return asset.temperature != null
          ? { color: temperatureColor(asset.temperature, 0.75) }
          : {};
      default:
        return {};
    }
  }

  // Download function
  function downloadData() {
    const selectedOption =
      columnOptions.find((opt) => opt.value === selectedColumn)?.label ||
      selectedColumn;
    const csvData = itemsSorted.map((asset) => ({
      [label]: asset.fullFrontendId,
      [selectedOption]: getCellValue(asset),
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map((row) => Object.values(row).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${labelPlural.toLowerCase()}_${selectedColumn}_data.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const ctx = useCurrentAssets();
  const [data] = ctx ?? [{}];

  const assets = data.assets ?? [];

  // Get the selected asset type from filters instead of hardcoding logic
  const selectedType: AssetType = assetsFilters.assetType;
  const selectedAssets = assets.filter((asset) => asset.type === selectedType);

  // Dynamic labels based on selected asset type using the same logic as filters
  const labelFromAssetType: Record<AssetType, string> = {
    [AssetType.CONTAINER]: singular(me.config.top.labels.name),
    [AssetType.PCS]: 'PCS',
    [AssetType.STRING]: 'String',
    [AssetType.RACK]: singular(me.config.unit.labels.name),
    [AssetType.MODULE]: 'Module',
  };

  const labelPluralFromAssetType: Record<AssetType, string> = {
    [AssetType.CONTAINER]: me.config.top.labels.name[1], // Use plural form
    [AssetType.PCS]: 'PCS',
    [AssetType.STRING]: 'Strings',
    [AssetType.RACK]: me.config.unit.labels.name[1], // Use plural form
    [AssetType.MODULE]: 'Modules',
  };

  const label = labelFromAssetType[selectedType];
  const labelPlural = labelPluralFromAssetType[selectedType];

  const itemsSorted = useMemo(() => {
    let items = selectedAssets.slice();

    items = items.filter((asset) => {
      switch (selectedColumn) {
        case 'imbalance':
          if (asset.imbalanceSoc != null) {
            return (
              asset.imbalanceSoc >= viewFilters.imbalanceSoc.min &&
              asset.imbalanceSoc <= viewFilters.imbalanceSoc.max
            );
          }
          return true;
        case 'accureSoc':
          if (asset.accureSoc != null) {
            return (
              asset.accureSoc >= viewFilters.accureSoc.min &&
              asset.accureSoc <= viewFilters.accureSoc.max
            );
          }
          return true;
        case 'bmsSoc':
          if (asset.bmsSoc != null) {
            return (
              asset.bmsSoc >= viewFilters.bmsSoc.min &&
              asset.bmsSoc <= viewFilters.bmsSoc.max
            );
          }
          return true;
        case 'temperature':
          if (asset.temperature != null) {
            return (
              asset.temperature >= viewFilters.temperature.min &&
              asset.temperature <= viewFilters.temperature.max
            );
          }
          return true;
        case 'alertStatus':
          return true;
        default:
          return true;
      }
    });

    items.sort((a, b) => {
      let aValue, bValue;
      const statusOrder: Record<string, number> = {
        Critical: 5,
        Tripped: 4,
        Warning: 3,
        Info: 2,
        OK: 1,
      };

      switch (selectedColumn) {
        case 'imbalance':
          aValue = a.imbalanceSoc;
          bValue = b.imbalanceSoc;
          break;
        case 'accureSoc':
          aValue = a.accureSoc;
          bValue = b.accureSoc;
          break;
        case 'bmsSoc':
          aValue = a.bmsSoc;
          bValue = b.bmsSoc;
          break;
        case 'socOffset':
          aValue = assetSocOffset(a);
          bValue = assetSocOffset(b);
          break;
        case 'temperature':
          aValue = a.temperature;
          bValue = b.temperature;
          break;
        case 'alertStatus':
          aValue = a.alertStatus ? (statusOrder[a.alertStatus] ?? 0) : 0;
          bValue = b.alertStatus ? (statusOrder[b.alertStatus] ?? 0) : 0;
          break;
        default:
          aValue = undefined;
          bValue = undefined;
      }

      if (aValue == null && bValue == null) {
        return a.fullFrontendId.localeCompare(b.fullFrontendId);
      }
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const diff = bValue - aValue;
      return diff !== 0
        ? diff
        : a.fullFrontendId.localeCompare(b.fullFrontendId);
    });
    return items;
  }, [
    selectedAssets,
    selectedColumn,
    viewFilters.imbalanceSoc,
    viewFilters.accureSoc,
    viewFilters.bmsSoc,
    viewFilters.temperature,
  ]);

  return (
    <div className="relative h-full">
      {/* Toggle button - always visible */}
      <button
        onClick={() => onToggle(!isOpen)}
        className={
          'absolute right-0 top-4 z-50 rounded-l-md border border-gray-200 bg-white p-2 shadow-sm transition-all duration-300 ease-in-out hover:bg-gray-50'
        }
        style={{
          right: isOpen ? '384px' : '0px',
        }}
        title={isOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        {isOpen ? (
          <ChevronRight className="size-4 text-gray-600" />
        ) : (
          <ChevronLeft className="size-4 text-gray-600" />
        )}
      </button>

      {/* Sidebar panel */}
      <div
        className={`absolute right-0 top-0 z-40 flex h-full w-96 flex-col border-l border-gray-200 bg-white transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          height: '100%',
          maxHeight: '100%',
        }}
      >
        {/* Header*/}
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Server className="size-4 text-gray-600" />
              <h2 className="text-sm font-medium text-gray-900">
                {labelPlural}
              </h2>
            </div>
            {itemsSorted.length > 0 && (
              <button
                onClick={downloadData}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                title="Download CSV"
              >
                <Download className="size-3" />
                Download
              </button>
            )}
          </div>
        </div>

        {/* Scrollable List */}
        <div className="flex-1 overflow-y-auto">
          {itemsSorted.length === 0 ? (
            <div className="px-4 py-3 text-xs text-gray-500">
              {`No ${labelPlural.toLowerCase()} match the current filter. Try adjusting your filters.`}
            </div>
          ) : (
            <Table bleed dense>
              <TableHead>
                <TableRow className="sticky top-0 z-10 bg-white">
                  <TableHeader className="min-w-[200px] max-w-[320px] whitespace-normal break-words pr-2">
                    {label}
                  </TableHeader>
                  <TableHeader className="min-w-[120px] max-w-[160px] pl-2 align-top">
                    {selectedColumnLabel}
                  </TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {itemsSorted.map((r) => (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <TableCell
                      className="line-clamp-2 min-w-[200px] max-w-[320px] whitespace-normal break-words pr-2 font-mono text-sm text-gray-900"
                      title={r.fullFrontendId}
                    >
                      {r.fullFrontendId}
                    </TableCell>
                    <TableCell
                      className="min-w-[120px] max-w-[160px] pl-2 text-sm"
                      style={getCellStyle(r)}
                    >
                      {getCellValue(r)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
